<script>
  import { onMount } from 'svelte';
  import { api } from '../lib/api.js';
  import TextField from './TextField.svelte';
  import Select from './Select.svelte';
  import DateField from './DateField.svelte';

  let { filter = $bindable({}), onApply, dateMin = '', dateMax = '' } = $props();

  let buckets = $state([]);
  // Default expanded once there's room for at least a 2-column layout (550px+).
  // Below that, collapse by default and show a "Filters" toggle.
  let expanded = $state(typeof window !== 'undefined' ? window.innerWidth >= 550 : true);

  // Count active (non-empty) filters for the collapsed-state badge.
  const activeCount = $derived(Object.values(filter).filter((v) => v !== '' && v != null).length);

  onMount(async () => {
    try {
      const r = await api.listBuckets();
      buckets = r.buckets || [];
    } catch (_) { /* nav-elsewhere will resurface auth errors */ }
  });

  const bucketOptions = $derived([
    { value: '',       label: 'All buckets' },
    { value: 'global', label: 'Global catch-all' },
    ...buckets.map((b) => ({ value: b.id, label: b.name, supporting: b.id })),
  ]);

  const methodOptions = [
    { value: '',        label: 'Any' },
    { value: 'GET',     label: 'GET' },
    { value: 'POST',    label: 'POST' },
    { value: 'PUT',     label: 'PUT' },
    { value: 'PATCH',   label: 'PATCH' },
    { value: 'DELETE',  label: 'DELETE' },
    { value: 'HEAD',    label: 'HEAD' },
    { value: 'OPTIONS', label: 'OPTIONS' },
  ];

  function apply() { onApply?.(filter); }
  function reset() {
    for (const k of Object.keys(filter)) filter[k] = '';
    apply();
  }
  // Wrapping the filter grid in a <form> means hitting Enter inside any of
  // the text inputs runs apply(). Tab between fields is already the browser
  // default for sibling inputs -- nothing to wire up.
  function onSubmit(e) {
    e.preventDefault();
    apply();
  }
</script>

<section class="bg-surface-container rounded-2xl p-4 mb-4">
  <button
    type="button"
    onclick={() => (expanded = !expanded)}
    class="w-full flex items-center gap-2 min-[550px]:hidden mb-3 text-on-surface-variant"
    aria-expanded={expanded}
  >
    <span class="material-symbols-outlined">tune</span>
    <span class="font-medium text-on-surface">Filters</span>
    {#if activeCount > 0}
      <span class="px-2 py-0.5 text-xs rounded-full bg-primary text-on-primary">{activeCount}</span>
    {/if}
    <span class="material-symbols-outlined ml-auto transition-transform" style:transform={expanded ? 'rotate(180deg)' : ''}>
      expand_more
    </span>
  </button>

  <form onsubmit={onSubmit}>
    <div class="grid grid-cols-1 min-[550px]:grid-cols-2 md:grid-cols-6 gap-4" class:hidden={!expanded}>
      <div class="md:col-span-2">
        <Select label="Bucket" bind:value={filter.bucket_id} options={bucketOptions} />
      </div>

      <Select label="Method" bind:value={filter.method} options={methodOptions} />

      <div class="md:col-span-2">
        <TextField label="Path contains" bind:value={filter.path} leadingIcon="route" />
      </div>
      <TextField label="Subject contains" bind:value={filter.subject} />

      <div class="md:col-span-2">
        <TextField label="Header name" bind:value={filter.header_key} leadingIcon="badge" />
      </div>
      <div class="md:col-span-2">
        <TextField label="Header value contains" bind:value={filter.header_value} />
      </div>
      <DateField label="From"  bind:value={filter.since} min={dateMin} max={filter.until || dateMax} />
      <DateField label="Until" bind:value={filter.until} min={filter.since || dateMin} max={dateMax} />
    </div>

    {#if expanded}
      <div class="flex justify-end gap-2 mt-4">
        <button type="button" onclick={reset} class="px-4 py-2 rounded-full text-sm text-on-surface-variant hover:bg-surface-container-high">
          Reset
        </button>
        <button type="submit" class="px-6 py-2.5 rounded-full text-sm font-medium bg-primary text-on-primary hover:opacity-90">
          Apply
        </button>
      </div>
    {/if}
  </form>
</section>
